import React, {useState, useContext, useEffect} from 'react';
import {multiFactor} from 'firebase/auth';
import {doc, getDoc} from 'firebase/firestore';

import {isHiddenAccount} from '@embertracking/common';
import {auth, db} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {hasPermission} from '-/data/utils';

import AppBanner from './AppBanner';
import MFAEnrollment from './MFAEnrollment';

import Collapse from '@mui/material/Collapse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DISMISS_KEY_PREFIX = 'mfa_banner_dismissed_';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

const SENSITIVE_PERMISSIONS = ['admin', 'payroll.read', 'payroll.approve'];

function hasSensitivePermission(user) {
    return SENSITIVE_PERMISSIONS.some(p => hasPermission(user, p));
}

function hasMfaEnrolled() {
    const user = auth.currentUser;
    if (!user) return false;
    return multiFactor(user).enrolledFactors.length > 0;
}

function isDismissed(uid) {
    const key = DISMISS_KEY_PREFIX + uid;
    const dismissed = localStorage.getItem(key);
    if (!dismissed) return false;

    const timestamp = parseInt(dismissed, 10);
    if (Date.now() - timestamp > DISMISS_DURATION) {
        localStorage.removeItem(key);
        return false;
    }

    return true;
}

export default function MFABanner() {
    const {currentUser} = useContext(UserContext);
    const [visible, setVisible] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const uid = currentUser?.uid;
    const isStation = currentUser?.isStation;
    // Hidden accounts (the operator, a hub user signed in from a hub) have no
    // password to protect with a second factor — their only way in is an
    // api-minted session — so the nudge is noise.
    const isHidden = isHiddenAccount(currentUser);
    // Collapsed to a boolean so it can be an effect dependency — `permissions`
    // is an object that gets a new identity on every bootstrap stage.
    const isSensitive = !!currentUser && hasSensitivePermission(currentUser);

    useEffect(() => {
        if (!uid || isStation || isHidden) {
            setVisible(false);
            return;
        }

        if (!isSensitive) {
            setVisible(false);
            return;
        }

        let cancelled = false;
        (async () => {
            // Auth refreshes the persisted user before App bootstraps this uid.
            // Reloading again here restarts Firestore's just-opened listeners,
            // which can strand pages such as Gear on their initial loading state.
            // Enrollment below updates the Auth user and hides this banner itself.
            if (hasMfaEnrolled()) {
                setVisible(false);
                return;
            }

            if (isDismissed(uid)) {
                setVisible(false);
                return;
            }

            // Permanent, cross-device suppression via userPreferences (the demo seed
            // sets this so the security nudge doesn't clutter screencaps). Distinct from
            // the 7-day localStorage dismissal above.
            try {
                const prefsSnap = await getDoc(doc(db, 'userPreferences', uid));
                if (cancelled) return;
                if (prefsSnap.exists() && prefsSnap.data().mfaBannerDismissed === true) {
                    setVisible(false);
                    return;
                }
            } catch {
                // Preference unavailable (offline/rules); fall through to show.
            }

            setVisible(true);
        })();

        return () => {
            cancelled = true;
        };
        // Bootstrap and profile snapshots replace currentUser; only changes
        // to these values need to re-evaluate the nudge and its preferences.
    }, [uid, isStation, isHidden, isSensitive]);

    return (
        <>
            <Collapse in={visible} appear timeout={300} unmountOnExit>
                <AppBanner
                    tone="warning"
                    icon={<WarningAmberIcon />}
                    action={{label: 'Set Up Now', onClick: () => setDialogOpen(true), testId: 'mfa-banner-setup'}}
                    testId="mfa-banner"
                >
                    Your account has elevated privileges. Please enable two-factor authentication for added security.
                </AppBanner>
            </Collapse>
            <MFAEnrollment
                dialogOnly
                phone={currentUser?.phone}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onEnrolled={() => setVisible(false)}
            />
        </>
    );
}
